<template>
  <!-- Router link -->
  <router-link v-if="!!to" v-slot="{ navigate, href }" :to="to" custom>
    <component
      :is="anchor"
      :href="href"
      @click.native="$e => onClick($e, navigate)"
    />
  </router-link>
  <!-- Standard link -->
  <component :is="anchor" v-else @click.native="$e => onClick($e)" />
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { PropType, CreateElement } from "vue";
import type { Location } from "vue-router";

export default defineComponent({
  name: "ULink",
  props: {
    to: { type: Object as PropType<Location>, default: undefined },
    iconLeft: { type: String, default: null },
    iconLeftClass: { type: String, default: null },
    iconRight: { type: String, default: null },
    iconRightClass: { type: String, default: null }
  },
  computed: {
    disabled() {
      const { $attrs } = this;
      return "disabled" in $attrs && ($attrs.disabled as unknown) !== false;
    },
    anchor() {
      const slot = this.$scopedSlots?.default && this.$scopedSlots.default({});
      return {
        render: (h: CreateElement) =>
          h(
            "a",
            {
              class: "u-link",
              attrs: {
                disabled: this.disabled
              }
            },
            [
              this.iconLeft &&
                h("b-icon", {
                  class: ["has-margin-right-25", this.iconLeftClass],
                  props: {
                    icon: this.iconLeft,
                    size: "is-small"
                  }
                }),
              h("u", {}, slot),
              this.iconRight &&
                h("b-icon", {
                  class: ["has-margin-left-25", this.iconRightClass],
                  props: {
                    icon: this.iconRight,
                    size: "is-small"
                  }
                })
            ]
          )
      };
    }
  },
  methods: {
    onClick($e: Event, navigate?: Function) {
      // If disabled, exit and prevent default behaviour
      if (this.disabled) return $e.preventDefault();
      // Call navigate function if passed from router-link
      if (typeof navigate === "function") navigate($e);
      // Otherwise, prevent default behaviour (unless outbound)
      else if (!this.$attrs.href) $e.preventDefault();
      // Emit click event
      this.$emit("click", $e);
    }
  }
});
</script>

<style lang="scss" scoped>
a.u-link {
  color: inherit;
  &[disabled] {
    opacity: 0.25;
    cursor: not-allowed;
  }
}
</style>
